import type { RouterHistory } from 'vue-router'
import { HasIndex, urlWithHash, validInt, validStr } from 'booksprout'

/** What a url write needs from the router: its history, which records the url every entry holds. */
export interface UrlStateRouter {
  options: { history: Pick<RouterHistory, 'location' | 'push' | 'replace'> }
}

/**
 * Writes `location` — router-relative, e.g. `/review-copies#?page=3` — as a new history entry without
 * navigating: no guards, no scroll, nothing re-mounts. That is what the plain `history.pushState` it
 * replaces did.
 *
 * It has to go through the router's history rather than the browser's. vue-router 4 keeps its own record
 * of the url each entry holds, and before every push it rewrites the current entry back to that record.
 * A url written behind its back is therefore undone the moment the reader opens anything, and Back lands
 * on the list without its page, tab or search. vue-router 3 never touched the current entry, which is why
 * the plain write worked until the move to Vue 3. [sc-28977]
 */
export const pushUrlState = (router: UrlStateRouter, location: string): void => {
  router.options.history.push(location)
}

/**
 * `pushUrlState` for a url that takes the place of the entry the visitor is on rather than adding one:
 * a create screen becoming `/update/:id` once its first save lands. Written past the router, the author's
 * next click put `/create` back, and Back from wherever it led opened a blank form. [sc-28993]
 */
export const replaceUrlState = (router: UrlStateRouter, location: string): void => {
  router.options.history.replace(location)
}

/**
 * The url the visitor is on with its hash set to `value` — a wizard's step, a tab — as a new history
 * entry, so that a refresh, or a Back from wherever they go next, opens on it again. [sc-28993]
 */
export const pushUrlHash = (router: UrlStateRouter, value: string | number): void => {
  pushUrlState(router, urlWithHash(router.options.history.location, value))
}

/**
 * Reading list filters out of the url and writing them back.
 *
 * `applyUrlFilterParam` assigns onto the object it is handed rather than returning a value, because
 * the list pages read a whole set of query params into one filter object; only params that are
 * actually present are written, so an absent param leaves the existing default alone.
 *
 * `setUrlFilterParams` writes them to the hash — or the query string in the Capacitor build, where
 * the hash is already spoken for by the router — through `pushUrlState`, so they survive a Back.
 */
export function useUrlFilters (isMobileApp: boolean, router: UrlStateRouter) {
  const applyUrlFilterParam = (
    assignToVar: HasIndex,
    propName: string,
    value: any,
    type: 'string' | 'intArray' | 'array' | 'int' = 'string'
  ) => {
    switch (type) {
      case 'string':
        if (validStr(value)) {
          assignToVar[propName] = value.toString()
        }
        break
      case 'intArray':
        if (validStr(value) && value !== 'undefined') {
          assignToVar[propName] = value.split(',').map((m: string) => parseInt(m.trim()))
        }
        break
      case 'array':
        if (validStr(value) && value !== 'undefined') {
          assignToVar[propName] = value.split(',').map((m: string) => m.trim())
        }
        break
      case 'int':
        if (validInt(value)) {
          assignToVar[propName] = parseInt(value)
        }
    }
  }

  const setUrlFilterParams = (data: any) => {
    pushUrlState(router, urlWithHash(router.options.history.location, data, isMobileApp ? '?' : '#'))
  }

  return {
    applyUrlFilterParam,
    setUrlFilterParams
  }
}
